import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-checkbox')
export class MdcCheckbox extends LitElement {
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) checked = false
  @property({ type: String }) label = ''
  @property({ type: Object }) validation?: { status: 'error' | 'success'; message?: string }

  static styles = css`
		:host {
			display: inline-block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-checkbox__wrapper {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-012-and-half, 0.125rem);
		}

		.mdc-checkbox {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-050, 0.5rem);
			cursor: pointer;
			user-select: none;
		}

		.mdc-checkbox__input {
			position: absolute;
			opacity: 0;
			width: 0;
			height: 0;
		}

		.mdc-checkbox__box {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 1.25rem;
			height: 1.25rem;
			border: 1px solid var(--color-functional-gray-400);
			border-radius: var(--radius-xs, 0.25rem);
			background-color: var(--color-functional-gray-white);
			transition: background-color 0.2s, border-color 0.2s;
			flex-shrink: 0;
		}

		.mdc-checkbox__input:checked + .mdc-checkbox__box {
			background-color: var(--color-functional-gray-black);
			border-color: var(--color-functional-gray-black);
		}

		.mdc-checkbox__input:checked + .mdc-checkbox__box mdc-icon {
			opacity: 1;
			visibility: visible;
			color: var(--color-functional-gray-white);
		}

		.mdc-checkbox__box mdc-icon {
			display: block;
			opacity: 0;
			visibility: hidden;
			width: 0.75rem;
			height: 0.75rem;
		}

		.mdc-checkbox__input:disabled + .mdc-checkbox__box {
			background-color: var(--color-functional-gray-50);
			border-color: var(--color-functional-gray-200);
			cursor: not-allowed;
		}

		.mdc-checkbox__input:disabled ~ .mdc-checkbox__label {
			color: var(--color-functional-gray-300);
			cursor: not-allowed;
		}

		.mdc-checkbox__input[aria-invalid='true'] + .mdc-checkbox__box {
			border-color: var(--color-functional-red-500);
		}

		.mdc-checkbox__label {
			font-size: var(--text-087-and-half, 0.875rem);
			font-weight: 450;
			line-height: 1.3;
			color: var(--color-functional-gray-black);
		}

		.mdc-checkbox__validation-message {
			font-size: var(--text-075, 0.75rem);
			font-weight: 450;
			line-height: 1.3;
		}

		.mdc-checkbox__validation-message--error {
			color: var(--color-functional-red-500);
		}

		.mdc-checkbox__validation-message--success {
			color: var(--color-functional-gray-600);
		}
	`

  private handleChange(e: Event) {
    const input = e.target as HTMLInputElement
    this.checked = input.checked
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { checked: this.checked },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    return html`
			<div class="mdc-checkbox__wrapper">
				<label class="mdc-checkbox">
					<input
						type="checkbox"
						class="mdc-checkbox__input"
						?checked=${this.checked}
						?disabled=${this.disabled}
						aria-invalid=${this.validation?.status === 'error' ? 'true' : undefined}
						@change=${this.handleChange}
					/>
					<div class="mdc-checkbox__box">
						<mdc-icon name="checkmark"></mdc-icon>
					</div>
					${this.label ? html`<span class="mdc-checkbox__label">${this.label}</span>` : ''}
				</label>
				${
          this.validation?.message
            ? html`
							<span class="mdc-checkbox__validation-message mdc-checkbox__validation-message--${this.validation.status}">
								${this.validation.message}
							</span>
						`
            : ''
        }
			</div>
		`
  }
}
